/**
 * AuthComplete — landing page for the Keycloak OIDC callback.
 *
 * The backend redirects here with either an opaque single-use completion code or a specific error
 * code — never a token, so no session material reaches browser history or proxy logs. Sibling to
 * PlatformBridgeAuth (the platform-bridge consumer); kept separate so the OIDC issuer is decoupled
 * from the bridge flow.
 *
 * URL: /auth/complete?code=<opaqueCode>  |  /auth/complete?error=<reason>
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import { completeOidcLogin } from '../services/api';

/** Distinct copy per backend rejection reason — the user sees a sentence, never a raw code. */
const MESSAGES: Record<string, string> = {
  not_provisioned:
    'That account is not set up for PRISM. Ask your administrator to add you, then sign in again.',
  identity_mismatch:
    'This PRISM account is linked to a different sign-in. Ask your administrator to check which account you should use.',
  unverified_email:
    'Your email address has not been verified with the identity provider. Verify it and try again.',
  idp_unavailable:
    'The identity provider could not be reached. Please try again in a few minutes.',
  sign_in_failed:
    'Sign-in did not complete. Please try signing in again.',
  admission_failed:
    'We could not finish setting up your session. Please try again, or contact your administrator if this continues.',
};

export default function AuthComplete() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const errCode = searchParams.get('error');
    if (errCode) {
      setError(MESSAGES[errCode] ?? 'Sign-in failed. Please try again.');
      return;
    }
    const code = searchParams.get('code');
    if (!code) {
      setError('Sign-in failed. Please try again.');
      return;
    }
    // completeOidcLogin persists prism_token / prism_user itself, matching its sibling
    // login functions in api.ts — do not duplicate that here.
    completeOidcLogin(code)
      .then(() => navigate('/', { replace: true }))
      .catch(() => setError('Your sign-in could not be completed. Please try signing in again.'));
  }, [searchParams, navigate]);

  if (error) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2, p: 3 }}>
        <Alert role="alert" severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
        <Button variant="text" onClick={() => navigate('/login', { replace: true })}>
          Back to sign in
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 2 }}>
      <CircularProgress />
      <Typography variant="body1" color="text.secondary">
        Signing you in…
      </Typography>
    </Box>
  );
}
